import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Button, Typography, Grid, InputLabel, CircularProgress } from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "../assets/logo.jpg";
import "../components/App.css";
import axios from "axios";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../config/config";
import BevHeader from "../components/BevHeader";
import BevFooter from "../components/BevFooter";
import PasswordField from "../components/PasswordField";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!token) {
      toast.error("Invalid reset link. Please request a new one.");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/reset-password`, {
        token,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      toast.success(response.data.message);
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.error || "Unable to reset password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <>
        <BevHeader />
        <div className="login-container">
          <div className="login-form">
            <Typography variant="h6" textAlign="center" marginBottom="20px">
              Invalid or missing reset link
            </Typography>
            <Link className="login-link" to="/forgot-password">Request a new reset link</Link>
          </div>
        </div>
        <BevFooter />
      </>
    );
  }

  return (
    <>
      <BevHeader />
      <div className="login-container">
        <div className="login-form">
          <img src={Logo} alt="Logo" width={120} className="form-logo" />
          <Typography variant="h6" component="h2" gutterBottom textAlign="center" marginBottom="20px" paddingBottom="10px" borderBottom="1px solid #ccc">
            Reset Password
          </Typography>
          <Formik
            initialValues={{ newPassword: "", confirmPassword: "" }}
            validationSchema={Yup.object({
              newPassword: Yup.string().required("Password is required").min(8, "Password must be at least 8 characters"),
              confirmPassword: Yup.string()
                .required("Please confirm your password")
                .oneOf([Yup.ref("newPassword")], "Passwords must match"),
            })}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="newPassword">New Password</InputLabel>
                    <Field
                      name="newPassword"
                      component={PasswordField}
                      size="small"
                      variant="outlined"
                      fullWidth
                      disabled={isSubmitting}
                    />
                    <ErrorMessage name="newPassword" component="div" className="login-error" />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="confirmPassword">Confirm Password</InputLabel>
                    <Field
                      name="confirmPassword"
                      component={PasswordField}
                      size="small"
                      variant="outlined"
                      fullWidth
                      disabled={isSubmitting}
                    />
                    <ErrorMessage name="confirmPassword" component="div" className="login-error" />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="success"
                      fullWidth
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                      {isSubmitting ? "Resetting..." : "Reset Password"}
                    </Button>
                  </Grid>
                  <Grid item xs={12} className="login-links">
                    <Link className="login-link" to="/login">Back to Login</Link>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <BevFooter />
    </>
  );
};

export default ResetPassword;
